/**
 * studio-app-route-plugin — the SCHEMA-FREE route-only sibling of
 * `studioAppPlugin` (#705 S2).
 *
 * `studioAppPlugin(model)` runs the full `generate(model)` at plugin-construction
 * time, dragging the model's schema literals + the generator + the strand kernel
 * onto whatever chunk composes it — for the studio's EAGER entry, that is the
 * whole reclaim target (#705). But the router (gate-4, boot, navigation) needs
 * only a cluster's ROUTE TABLE + its (lazy) editor map, and the route table is a
 * PURE projection of a schema-free {@link RouteDescriptor} (`projectRouteEntities`,
 * proven byte-identical to `generate()`'s route entities). `studioAppRoutePlugin`
 * builds the SAME plugin shape from that descriptor + a LAZY model loader, so the
 * cluster's route entities land on the entry with ZERO schema / generator weight,
 * while the generated editors (which DO need the model) load behind `React.lazy`
 * off the entry.
 *
 * This is the entry-side plugin the #705 cutover (S3) composes. It shares the SAME
 * route-placement rewrite (`applyRoutePlacement`) and the SAME lazy-editor builder
 * as `studioAppPlugin`, so a cluster's eager route table + editor resolution are
 * byte-identical whether composed full or route-only.
 *
 * ## Why the plugin ALSO emits stub `component` entities
 *
 * A `route` entity carries a `routes-to` ref at its editor's `component/{id}`
 * target. The v3 cross-ref validator (`GenomeBuilderV3.build()` →
 * `sdk/src/genome/validate.ts`) requires EVERY ref target to exist as an entity —
 * so a routes-only genome (routes → components that don't exist) FAILS the
 * VALIDATING build, which the studio drives on the eager plugin set (the
 * validating-build assertion in `genome.test.ts`) AND at runtime through the
 * validating restage/install recompose (`applyRestagePayload` /
 * `installArtifact`). So this plugin emits a routeless STUB `component` entity per
 * surviving `routes-to` target — a `{ kind:'component', meta:{ id } }` with no
 * refs, no schema, no store — so the composed genome is a clean, resolvable graph
 * while carrying NONE of the generated cluster's heavy entities (schema/store/
 * service/action/query/effect) or `generate()` weight. `resolveRoutes` resolves
 * the route's editor via `plugin.editors[id]` (NOT the stub), so the stub is a
 * validator-satisfying placeholder only. The `no-schema-in-entry` gate
 * (`no-generated-schema-in-entry.test.ts`) pins that the generated namespaces
 * contribute ONLY these `route` + stub-`component` kinds on the eager entry.
 *
 * @module @ubiclouds/v5-studio/genome/studio-app-route-plugin
 */

import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { EntityDef } from '@ubiclouds/v5-sdk/genome';
import {
  projectRouteEntities,
  type DomainModel,
  type RouteDescriptor,
} from '@ubiclouds/v5-genome-gen';
import {
  applyRoutePlacement,
  type RoutePlacement,
  type StudioAppTransport,
} from './studio-app-route-placement.js';
import {
  buildLazyEditors,
  editorIdsForNodes,
  makeEditorMapLoader,
} from './studio-app-lazy-editors.js';

/**
 * Options for {@link studioAppRoutePlugin} — the shared {@link RoutePlacement}
 * re-home knobs (identical semantics to `StudioAppPluginOptions`) plus the
 * transport discriminant and the LAZY model loader the editors need.
 */
export interface StudioAppRoutePluginOptions extends RoutePlacement {
  /**
   * Which data transport the generated editors read through (#S-18b) — resolved
   * inside the deferred editor chunk. DEFAULT `'mock'`. See {@link StudioAppTransport}.
   */
  readonly transport?: StudioAppTransport;
  /**
   * Lazy loader of the cluster's full `DomainModel` — a dynamic-`import()` thunk
   * (`() => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.monzoModel)`)
   * so the model SCHEMA stays off the studio entry. Invoked only inside the
   * deferred editor chunk when a generated route first mounts; the route table
   * itself is projected from the schema-free descriptor, never from the model.
   */
  readonly loadModel: () => DomainModel | Promise<DomainModel>;
}

/**
 * Build the route-only studio plugin from a schema-free {@link RouteDescriptor}.
 * Its `entities()` are ONLY the placement-rewritten route entities (the router
 * needs nothing else); its `editors` are the SAME `React.lazy` wrappers as the
 * full plugin, resolved from the lazy model loader off the entry.
 */
export function studioAppRoutePlugin(
  descriptor: RouteDescriptor,
  options: StudioAppRoutePluginOptions,
): ComposableGenomePlugin {
  const transport: StudioAppTransport = options.transport ?? 'mock';

  // The RAW route entities — a pure projection of the descriptor, byte-identical
  // to `generate(model)`'s route entities (the `route-descriptor` gate). No
  // schema, no generator, no strand kernel.
  const routeEntities = projectRouteEntities(descriptor);

  // The generated editors: the SHARED lazy-editor builder over a LAZY model
  // loader, so the model schema loads off the entry with the heavy render chunk.
  const loadEditorMap = makeEditorMapLoader(options.loadModel, transport, options.routePrefix);
  const editors = buildLazyEditors(
    editorIdsForNodes(descriptor.nodes.map((n) => n.name)),
    loadEditorMap,
  );

  const plugin: ComposableGenomePlugin = {
    id: descriptor.id,
    name: descriptor.name,
    version: descriptor.version,
    trustTier: 'first-party',
    editors: Object.freeze(editors),
    entities(): EntityDef[] {
      // The SAME route-placement rewrite `studioAppPlugin` applies — so the eager
      // route table is byte-identical whether from the full graph or the descriptor.
      const placed = applyRoutePlacement(routeEntities, options);
      // A routeless STUB `component` per surviving `routes-to` target: the v3
      // validator (`.build()`) requires every ref target to exist, and the studio
      // validates the composed genome (the boot-set assertion + the restage/install
      // recompose). The stub carries NO schema/store/service — the generated
      // cluster's heavy entities stay off the entry — and `resolveRoutes` resolves
      // the editor from `plugin.editors[id]`, not this stub.
      return [...placed, ...componentStubsFor(placed)];
    },
  };
  return Object.freeze(plugin);
}

/**
 * The routeless `component` stubs a route-only plugin must emit so its
 * `routes-to` targets exist (validator integrity). One stub per UNIQUE surviving
 * target (the placement rewrite may `omitIndex` the `/` route, dropping its
 * `component/index` target — no orphan stub is minted).
 */
function componentStubsFor(routes: readonly EntityDef[]): EntityDef[] {
  const targets = new Set<string>();
  for (const route of routes) {
    for (const ref of route.refs ?? []) {
      if (ref.rel === 'routes-to') targets.add(ref.target);
    }
  }
  return [...targets].map((id) => ({ kind: 'component', meta: { id } }));
}
